// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { Button, DataTableBody } from "@insite/mobius/index";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import { tableProps } from "@store/Themes/common/props/table";
import {
    useBackOrdersParameter,
    useDeleteModalIsOpen,
    usePendingQuantity,
    useSelectedBackOrderItem,
    useUpdateModalIsOpen,
} from "@store/Framework/Store/Store/Data/BackOrders/BackOrdersHooks";
import loadBackOrders from "@store/Framework/Store/Store/Data/BackOrders/Handlers/LoadBackOrders";
import setDeleteModalIsOpen from "@store/Framework/Store/Store/Data/BackOrders/Handlers/SetDeleteModalIsOpen";
import setPendingQuantityHandler from "@store/Framework/Store/Store/Data/BackOrders/Handlers/SetPendingQuantity";
import setSelectedBackOrderItem from "@store/Framework/Store/Store/Data/BackOrders/Handlers/SetSelectedBackOrderItem";
import setUpdateModalIsOpen from "@store/Framework/Store/Store/Data/BackOrders/Handlers/SetUpdateModalIsOpen";
import updateBackOrderLine from "@store/Framework/Store/Store/Data/BackOrders/Handlers/UpdateBackOrderLine";
import BackOrdersRow from "@store/Library/Components/BackOrders/BackOrdersRow";
import React, { FC, useContext, useEffect } from "react";

type BackOrdersModalsProps = {};

export interface BackOrdersModalsStyles {
    deleteBackOrderModal?: TwoButtonModalStyles;
    updateModal?: ModalPresentationProps;
    updateQuantityConfirmButton?: ButtonPresentationProps;
    updateQuantityCancelButton?: ButtonPresentationProps;
    dataTable?: DataTableProps;
}

export const backOrdersModalsStyles: BackOrdersModalsStyles = {
    deleteBackOrderModal: {
        modal: { sizeVariant: "small" },
    },
    updateQuantityCancelButton: {
        variant: "secondary",
    },
    dataTable: tableProps.dataTable,
};

const styles = backOrdersModalsStyles;

const BackOrdersModals: FC<BackOrdersModalsProps> = () => {
    const dispatch = useAppDispatch();
    const toasterContext = useContext(ToasterContext);
    const backOrdersParameter = useBackOrdersParameter();
    const deleteModalIsOpen = useDeleteModalIsOpen();
    const updateModalIsOpen = useUpdateModalIsOpen();
    const selectedBackOrderItem = useSelectedBackOrderItem();
    const pendingQuantity = usePendingQuantity();

    useEffect(() => {
        if (updateModalIsOpen && selectedBackOrderItem?.backOrderQuantity) {
            const parsedQuantity = Number.parseInt(selectedBackOrderItem.backOrderQuantity, 10);
            dispatch(setPendingQuantityHandler({ pendingQuantity: parsedQuantity }));
        }
    }, [updateModalIsOpen, selectedBackOrderItem]);

    const closeDeleteModal = () => {
        dispatch(setDeleteModalIsOpen({ isOpen: false }));
        dispatch(setSelectedBackOrderItem({ backOrderItem: null }));
    };

    const handleDeleteConfirm = () => {
        if (!selectedBackOrderItem?.id) {
            toasterContext.addToast({
                body: translate("Unable to cancel back order item"),
                messageType: "danger",
            });
            return;
        }

        dispatch(
            updateBackOrderLine({
                id: selectedBackOrderItem.id,
                isCancelled: true,
                newQuantity: 0,
                onSuccess: () => {
                    closeDeleteModal();
                    dispatch(loadBackOrders(backOrdersParameter));
                    toasterContext.addToast({
                        body: translate("Back order cancelled successfully"),
                        messageType: "success",
                    });
                },
                onError: errorMessage => {
                    toasterContext.addToast({
                        body: errorMessage,
                        messageType: "danger",
                    });
                },
            }),
        );
    };

    const closeUpdateModal = () => {
        dispatch(setUpdateModalIsOpen({ isOpen: false }));
        dispatch(setSelectedBackOrderItem({ backOrderItem: null }));
        dispatch(setPendingQuantityHandler({ pendingQuantity: 0 }));
    };

    const handleUpdateQuantityConfirm = () => {
        dispatch(
            updateBackOrderLine({
                id: selectedBackOrderItem?.id ?? "",
                isCancelled: false,
                newQuantity: pendingQuantity,
                onSuccess: () => {
                    closeUpdateModal();
                    dispatch(loadBackOrders(backOrdersParameter));
                    toasterContext.addToast({
                        body: translate("Back order updated successfully"),
                        messageType: "success",
                    });
                },
                onError: errorMessage => {
                    toasterContext.addToast({
                        body: errorMessage,
                        messageType: "danger",
                    });
                },
            }),
        );
    };

    return (
        <>
            <TwoButtonModal
                modalIsOpen={deleteModalIsOpen}
                headlineText={translate("Delete Back Order")}
                messageText={translate("Are you sure you want to cancel this back order item?")}
                cancelButtonText={translate("Keep")}
                submitButtonText={translate("Delete")}
                onCancel={closeDeleteModal}
                onSubmit={handleDeleteConfirm}
                dataTestSelector="backOrdersList_deleteBackOrderModal"
                extendedStyles={styles.deleteBackOrderModal}
            />
            <Modal
                headline={translate("Edit Back Order")}
                isOpen={updateModalIsOpen}
                handleClose={closeUpdateModal}
                {...styles.updateModal}
                data-test-selector="backOrdersList_editBackOrderModal"
            >
                <StyledWrapper>
                    {selectedBackOrderItem && (
                        <DataTable {...styles.dataTable}>
                            <DataTableBody>
                                <BackOrdersRow backOrderItem={selectedBackOrderItem} editQty />
                            </DataTableBody>
                        </DataTable>
                    )}
                    <FormActions>
                        <Button
                            {...styles.updateQuantityConfirmButton}
                            onClick={handleUpdateQuantityConfirm}
                            data-test-selector="backOrdersList_updateConfirmButton"
                        >
                            {translate("Update")}
                        </Button>
                        <Button
                            {...styles.updateQuantityCancelButton}
                            onClick={closeUpdateModal}
                            data-test-selector="backOrdersList_updateCancelButton"
                        >
                            {translate("Cancel")}
                        </Button>
                    </FormActions>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default BackOrdersModals;
